import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Eraser, RefreshCw, Save, PenTool, Type, CheckCircle2, AlertCircle } from "lucide-react";
import { validateSignature, describeReason, serializeStrokes } from "@/lib/signatureValidate";

const TYPE_FONTS = [
    { id: "Caveat", label: "Caveat", style: { fontFamily: "Caveat, cursive" } },
    { id: "Dancing Script", label: "Dancing Script", style: { fontFamily: "'Dancing Script', cursive" } },
    { id: "Great Vibes", label: "Great Vibes", style: { fontFamily: "'Great Vibes', cursive" } },
];

let fontsInjected = false;
function ensureFonts() {
    if (fontsInjected || typeof document === "undefined") return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href =
        "https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&family=Dancing+Script:wght@400;700&family=Great+Vibes&display=swap";
    document.head.appendChild(link);
    fontsInjected = true;
}

export function SignaturePad({ field, submissionId, defaultSignerName, currentSignature, onSaved, disabled }) {
    const [mode, setMode] = useState(currentSignature?.method || "draw");
    const [signerName, setSignerName] = useState(currentSignature?.signer_name || defaultSignerName || "");
    const [typedText, setTypedText] = useState(currentSignature?.typed_text || defaultSignerName || "");
    const [font, setFont] = useState(currentSignature?.font || TYPE_FONTS[0].id);
    const [saving, setSaving] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const canvasRef = useRef(null);
    const drawingRef = useRef(false);
    const lastPointRef = useRef(null);
    const segmentsRef = useRef([]); // Phase SIG-VALIDATE — stroke buffer
    const startTsRef = useRef(null);
    const [validation, setValidation] = useState({ ok: false, reason: "empty", metrics: {} });

    useEffect(() => { ensureFonts(); }, []);

    // Setup canvas
    useEffect(() => {
        if (mode !== "draw") return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ratio = window.devicePixelRatio || 1;
        const w = canvas.clientWidth;
        const h = 160;
        canvas.width = w * ratio;
        canvas.height = h * ratio;
        const ctx = canvas.getContext("2d");
        ctx.scale(ratio, ratio);
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, w, h);
        ctx.strokeStyle = "#0f172a";
        ctx.lineWidth = 2.2;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        segmentsRef.current = [];
        startTsRef.current = null;
        setValidation({ ok: false, reason: "empty", metrics: {} });
    }, [mode]);

    const getPos = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const t = e.touches ? e.touches[0] : e;
        return { x: t.clientX - rect.left, y: t.clientY - rect.top };
    };

    const pushPoint = (p) => {
        const segs = segmentsRef.current;
        if (!segs.length) return;
        const ts = startTsRef.current == null ? 0 : (performance.now() - startTsRef.current);
        segs[segs.length - 1].push({ x: p.x, y: p.y, t: ts });
        // Recompute validation on every point for live feedback.
        const v = validateSignature({ segments: segs, method: "draw" });
        setValidation(v);
    };

    const startDraw = (e) => {
        if (disabled) return;
        e.preventDefault();
        drawingRef.current = true;
        if (startTsRef.current == null) startTsRef.current = performance.now();
        // Open a new segment.
        segmentsRef.current.push([]);
        lastPointRef.current = getPos(e);
        pushPoint(lastPointRef.current);
    };

    const moveDraw = (e) => {
        if (!drawingRef.current) return;
        e.preventDefault();
        const ctx = canvasRef.current.getContext("2d");
        const p = getPos(e);
        const last = lastPointRef.current;
        ctx.beginPath();
        ctx.moveTo(last.x, last.y);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
        lastPointRef.current = p;
        pushPoint(p);
    };

    const endDraw = () => {
        drawingRef.current = false;
    };

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        segmentsRef.current = [];
        startTsRef.current = null;
        setValidation({ ok: false, reason: "empty", metrics: {} });
        setSubmitError(null);
    };

    const submittable = (() => {
        if (disabled || saving) return false;
        if (!signerName.trim()) return false;
        if (mode === "draw") return validation.ok;
        return (typedText || "").trim().length > 0;
    })();

    const handleSave = async () => {
        if (!signerName.trim()) {
            toast.error("Please enter the signer's name");
            return;
        }
        // Re-run strict validation before submit (defense in depth).
        if (mode === "draw") {
            const v = validateSignature({ segments: segmentsRef.current, method: "draw" });
            setValidation(v);
            if (!v.ok) {
                setSubmitError(describeReason(v.reason));
                return;
            }
        } else if (!typedText.trim()) {
            toast.error("Please type your signature");
            return;
        }
        setSubmitError(null);
        setSaving(true);
        try {
            let payload;
            if (mode === "draw") {
                const dataUrl = canvasRef.current.toDataURL("image/png");
                const m = validation.metrics || {};
                payload = {
                    field_key: field.key,
                    signer_name: signerName,
                    signer_role: field.key === "signature_guarantor" ? "guarantor" : "customer",
                    method: "draw",
                    image_data_url: dataUrl,
                    stroke_data: serializeStrokes(segmentsRef.current),
                    // Task 6 — Approach-A compact fields the server validates
                    // independently as defense-in-depth.
                    signature_points: Math.round(m.pointCount || 0),
                    signature_bbox: { w_px: Math.round(m.bboxWidth || 0), h_px: Math.round(m.bboxHeight || 0) },
                    signature_duration_ms: Math.round(m.durationMs || 0),
                };
            } else {
                payload = {
                    field_key: field.key,
                    signer_name: signerName,
                    signer_role: field.key === "signature_guarantor" ? "guarantor" : "customer",
                    method: "type",
                    typed_text: typedText,
                    font,
                };
            }
            const { data } = await api.post(`/submissions/${submissionId}/signatures`, payload);
            toast.success(`${field.label} saved`);
            onSaved(data);
        } catch (err) {
            // Surface backend signature_invalid reason inline.
            const detail = err?.response?.data?.detail;
            const reason = (detail && typeof detail === "object" && detail.reason) || null;
            if (reason) {
                setSubmitError(describeReason(reason));
            } else {
                setSubmitError("Please draw a clearer signature. Try again or use the Clear button.");
            }
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const reSign = () => {
        if (mode === "draw") clearCanvas();
        else setTypedText(defaultSignerName || "");
    };

    const fontStyle = TYPE_FONTS.find((f) => f.id === font)?.style || TYPE_FONTS[0].style;

    return (
        <div data-testid={`signature-pad-${field.key}`} className="rounded-md border border-slate-200 bg-white p-5">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div>
                    <h4 className="font-medium text-slate-900">{field.label}</h4>
                    <p className="text-xs text-slate-500 mt-0.5">
                        {currentSignature ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700">
                                <CheckCircle2 size={12} /> Signed {new Date(currentSignature.signed_at).toLocaleString()}
                            </span>
                        ) : (
                            "Draw or type your signature below."
                        )}
                    </p>
                </div>
                <div className="inline-flex rounded-md border border-slate-200 p-0.5 bg-slate-50">
                    <button
                        type="button"
                        onClick={() => setMode("draw")}
                        data-testid={`signature-${field.key}-mode-draw`}
                        disabled={disabled}
                        className={`px-3 py-1 text-xs font-medium rounded inline-flex items-center gap-1.5 transition-colors ${
                            mode === "draw" ? "bg-blue-900 text-white" : "text-slate-600 hover:text-slate-900"
                        }`}
                    >
                        <PenTool size={12} /> Draw
                    </button>
                    <button
                        type="button"
                        onClick={() => setMode("type")}
                        data-testid={`signature-${field.key}-mode-type`}
                        disabled={disabled}
                        className={`px-3 py-1 text-xs font-medium rounded inline-flex items-center gap-1.5 transition-colors ${
                            mode === "type" ? "bg-blue-900 text-white" : "text-slate-600 hover:text-slate-900"
                        }`}
                    >
                        <Type size={12} /> Type
                    </button>
                </div>
            </div>

            <div className="space-y-3">
                <div className="space-y-1.5">
                    <Label htmlFor={`signer_${field.key}`} className="text-xs text-slate-600">Signer name</Label>
                    <Input
                        id={`signer_${field.key}`}
                        data-testid={`signature-${field.key}-name`}
                        value={signerName}
                        onChange={(e) => setSignerName(e.target.value)}
                        disabled={disabled}
                    />
                </div>

                {mode === "draw" ? (
                    <div>
                        <canvas
                            ref={canvasRef}
                            data-testid={`signature-${field.key}-canvas`}
                            onMouseDown={startDraw}
                            onMouseMove={moveDraw}
                            onMouseUp={endDraw}
                            onMouseLeave={endDraw}
                            onTouchStart={startDraw}
                            onTouchMove={moveDraw}
                            onTouchEnd={endDraw}
                            className="w-full h-[160px] rounded-md border border-dashed border-slate-300 bg-white touch-none cursor-crosshair"
                            style={{ touchAction: "none" }}
                        />
                        {/* Phase SIG-VALIDATE — live indicator + inline error */}
                        <div className="mt-1.5 flex items-center justify-between">
                            {validation.ok ? (
                                <span
                                    data-testid={`signature-${field.key}-live-valid`}
                                    className="inline-flex items-center gap-1.5 text-xs text-emerald-700 font-medium"
                                >
                                    <CheckCircle2 size={12} /> Valid signature
                                </span>
                            ) : (
                                <span
                                    data-testid={`signature-${field.key}-live-invalid`}
                                    className="inline-flex items-center gap-1.5 text-xs text-slate-500"
                                >
                                    <AlertCircle size={12} /> {validation.reason === "empty" ? "Draw to begin" : "Needs more detail"}
                                </span>
                            )}
                            <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={clearCanvas}
                                disabled={disabled}
                                data-testid={`signature-${field.key}-clear`}
                                className="gap-1.5"
                            >
                                <Eraser size={14} /> Clear
                            </Button>
                        </div>
                        {submitError && (
                            <p
                                data-testid={`signature-${field.key}-submit-error`}
                                className="mt-1.5 text-xs text-red-700 bg-red-50 border border-red-100 rounded px-2 py-1.5"
                            >
                                {submitError}
                            </p>
                        )}
                    </div>
                ) : (
                    <div className="space-y-2">
                        <div className="grid grid-cols-3 gap-2">
                            {TYPE_FONTS.map((f) => (
                                <button
                                    type="button"
                                    key={f.id}
                                    onClick={() => setFont(f.id)}
                                    data-testid={`signature-${field.key}-font-${f.id.replace(/\s/g, "-")}`}
                                    disabled={disabled}
                                    className={`px-2 py-2 text-xs rounded-md border transition-colors ${
                                        font === f.id ? "border-blue-900 bg-blue-50" : "border-slate-200 hover:bg-slate-50"
                                    }`}
                                    style={f.style}
                                >
                                    <span className="text-lg">{f.label}</span>
                                </button>
                            ))}
                        </div>
                        <Input
                            data-testid={`signature-${field.key}-text`}
                            placeholder="Type your full name"
                            value={typedText}
                            onChange={(e) => setTypedText(e.target.value)}
                            disabled={disabled}
                        />
                        <div
                            className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-center min-h-[80px] flex items-center justify-center"
                            data-testid={`signature-${field.key}-preview`}
                        >
                            <span className="text-3xl text-slate-900" style={fontStyle}>
                                {typedText || "Your signature"}
                            </span>
                        </div>
                    </div>
                )}

                {currentSignature && (
                    <div className="rounded-md bg-slate-50 border border-slate-200 p-3 flex items-center justify-between gap-3" data-testid={`signature-${field.key}-current`}>
                        <div className="flex items-center gap-3 min-w-0">
                            {currentSignature.method === "draw" && currentSignature.image_data_url ? (
                                <img
                                    src={currentSignature.image_data_url}
                                    alt="Signature"
                                    data-testid={`signature-${field.key}-thumb`}
                                    className="h-12 w-32 object-contain bg-white border border-slate-200 rounded"
                                />
                            ) : (
                                <span
                                    className="h-12 w-32 flex items-center justify-center text-xl bg-white border border-slate-200 rounded"
                                    style={{ fontFamily: TYPE_FONTS.find((f) => f.id === currentSignature.font)?.style.fontFamily }}
                                    data-testid={`signature-${field.key}-thumb`}
                                >
                                    {currentSignature.typed_text}
                                </span>
                            )}
                            <div className="min-w-0">
                                <div className="text-sm font-medium text-slate-900 truncate">{currentSignature.signer_name}</div>
                                <div className="text-xs text-slate-500">
                                    {currentSignature.method} · {new Date(currentSignature.signed_at).toLocaleDateString()}
                                </div>
                            </div>
                        </div>
                        {!disabled && (
                            <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={reSign}
                                data-testid={`signature-${field.key}-resign`}
                                className="gap-1.5"
                            >
                                <RefreshCw size={14} /> Re-sign
                            </Button>
                        )}
                    </div>
                )}

                {!disabled && (
                    <Button
                        type="button"
                        onClick={handleSave}
                        disabled={!submittable}
                        data-testid={`signature-${field.key}-save`}
                        className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                    >
                        <Save size={14} /> {saving ? "Saving…" : currentSignature ? "Update signature" : "Save signature"}
                    </Button>
                )}
            </div>
        </div>
    );
}
